<template>
  <div class="firmware-list" >
    <template v-if="isFirmwareListLoading">
      <div class="not-searched">
        <label>
          {{ i18n.global.t('pages.common.list.reading') }}
        </label>
      </div>
    </template>
    <ResizableList
      scrollAreaSelector=".v-table__wrapper"
    >
      <div class="table-scrollbar">
        <v-table>
        <thead>
          <tr class="table-header-row">
            <th>
              <table-sort
                :column-title="i18n.global.t('pages.firmwareList.list.id')"
                :sort-field="sortField.ORDER_BY_FIRMWARE_ID"
                :current-sort-field="firmwareListSort.sort"
                :current-sort-type="firmwareListSort.sort_type"
                @change-sort="changeSort"
                @toggle-sort="toggleSort"
              />
            </th>
            <th>{{ i18n.global.t('pages.firmwareList.version') }}</th>
            <th>
              <table-sort
                :column-title="i18n.global.t('pages.firmwareList.list.typeNumber')"
                :sort-field="sortField.ORDER_BY_DEVICE_TYPE"
                :current-sort-field="firmwareListSort.sort"
                :current-sort-type="firmwareListSort.sort_type"
                @change-sort="changeSort"
                @toggle-sort="toggleSort"
              />
            </th>
            <th>
              <table-sort
                :column-title="i18n.global.t('pages.firmwareList.valid')"
                :sort-field="sortField.ORDER_BY_ACTIVE"
                :current-sort-field="firmwareListSort.sort"
                :current-sort-type="firmwareListSort.sort_type"
                @change-sort="changeSort"
                @toggle-sort="toggleSort"
              />
            </th>
            <th>
              <table-sort
                :column-title="i18n.global.t('pages.firmwareList.list.updateAt')"
                :sort-field="sortField.ORDER_BY_UPDATED_AT"
                :current-sort-field="firmwareListSort.sort"
                :current-sort-type="firmwareListSort.sort_type"
                @change-sort="changeSort"
                @toggle-sort="toggleSort"
              />
            </th>
            <th>
              <table-sort
                :column-title="i18n.global.t('pages.firmwareList.list.updateBy')"
                :sort-field="sortField.ORDER_BY_UPDATED_USER"
                :current-sort-field="firmwareListSort.sort"
                :current-sort-type="firmwareListSort.sort_type"
                @change-sort="changeSort"
                @toggle-sort="toggleSort"
              />
            </th>
            <th>
              {{ i18n.global.t('pages.firmwareList.list.edit') }}
            </th>
          </tr>
          </thead>
          <tbody>
            <template v-if="firmwares.length > 0" >
              <tr
                v-for="firmware in firmwares"
                :key="firmware.id"
              >
                <td>{{ firmware.id }}</td>
                <td>{{ firmware.version }}</td>
                <td>{{ firmware.device_types.length > 0 ? firmware.device_types.map(deviceType=>deviceType.type).join(', ') : '-' }}</td>
                <td>{{ firmware.active ? i18n.global.t('pages.firmwareList.valid') : i18n.global.t('pages.firmwareList.invalid') }}</td>
                <td>{{ firmware.updated_at ? formatDateTimeWithSeconds(firmware.updated_at) : '-' }}</td>
                <td>{{ firmware.updated_by || '-' }}</td>
                <td>
                  <button
                    @click="$emit('update-firmware-click', firmware.id)"
                    class="d-flex material-symbols-outlined"
                  >
                    edit_note
                  </button>
                </td>
              </tr>
            </template>
            <template v-else-if="!isFirmwareListLoading">
              <tr>
                <td colspan="9">
                  {{ i18n.global.t('pages.common.list.noData') }}
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </div>
    </ResizableList>
  </div>
</template>

<script lang="ts">

import {defineComponent, PropType} from "vue";
import ResizableList from '@/components/ResizableList.vue';
import firmware from "@/types/Firmware";
import {formatDateTimeWithSeconds} from "@/services/functions";
import {sortField} from "@/types/requests/getFirmwareListRequest";
import {sortType} from "@/services/consts";
import TableSort from "@/components/template/TableSort.vue";
import i18n from '@/lang/i18n';

export default defineComponent({
  setup() {
    return {
      formatDateTimeWithSeconds,
      sortField,
      i18n
    }
  },
  data() {
    return {
      firmwareListSort: {
        sort_type: undefined as string | undefined,
        sort: undefined as string | undefined,
      },
    }
  },
  props: {
    isFirmwareListLoading: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    firmwares: {
      type: Array as PropType<firmware[]>,
      required: true,
    },
  },
  emits: ['sort-change', 'update-firmware-click'],
  name: "FirmwareList",
  computed: {},
  watch: {
    firmwareListSort: {
      handler() {
        this.$emit('sort-change', this.firmwareListSort)
      },
      deep: true
    },
  },
  methods: {
    toggleSort( sortFiledValue:string ){

      let updatedSortField = false;
      if ( sortFiledValue !== this.firmwareListSort.sort ){
        updatedSortField = true
      }

      this.firmwareListSort.sort = sortFiledValue;
      if ( this.firmwareListSort.sort_type === undefined || updatedSortField ){
        this.firmwareListSort.sort_type = sortType.ASK
      } else {
        this.firmwareListSort.sort_type = this.firmwareListSort.sort_type === sortType.DESC ? sortType.ASK : sortType.DESC
      }
    },
    changeSort(sortFiledValue: string, needSortType: sortType): void {
      if ( this.firmwareListSort.sort == sortFiledValue && this.firmwareListSort.sort_type == needSortType ){
        return
      }
      this.firmwareListSort.sort = sortFiledValue;
      this.firmwareListSort.sort_type = needSortType
    },
  },
  components: {
    ResizableList,
    TableSort
  }
})
</script>

<style>
.firmware-list .v-table__wrapper {
  height: var(--resizable-height, var(--list-default-height));
  overflow: auto;
}
</style>
<style scoped>
.firmware-list {
  position: relative;
}
.firmware-list table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  min-width: 7em;
}
.firmware-list table .table-header-row th {
  z-index: 1;
}
.firmware-list table .table-header-row th:first-child {
  z-index: 2;
}
</style>
